// Response wire shapes for the Value Calculator routes (api.ts). Both sides import
// this file: api.ts checks each `res.json(...)` body against `z.input<...>` and
// api-client.ts parses the 2xx body with the same schema.

import { z } from 'zod';
import { MetricHistoryMapSchema } from './engine/metric-history';

const SnapshotSummarySchema = z.object({
  id: z.string(),
  captureType: z.string().nullable(),
  createdDate: z.string().nullable(),
  /** Decimal comes as string from Prisma. */
  totalAnnualValue: z.string().nullable(),
  headlineSummary: z.string().nullable(),
});
export type SnapshotSummary = z.infer<typeof SnapshotSummarySchema>;

const SnapshotMetricSchema = z.object({
  id: z.string(),
  value: z.string().nullable(),
  source: z.string().nullable(),
});
export type SnapshotMetric = z.infer<typeof SnapshotMetricSchema>;

const SnapshotDetailSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  captureType: z.string().nullable(),
  capturedById: z.string().nullable(),
  capturedRole: z.string().nullable(),
  accountId: z.string().nullable(),
  opportunityId: z.string().nullable(),
  customerName: z.string().nullable(),
  externalId: z.string().nullable(),
  storyJson: z.string().nullable(),
  totalAnnualValue: z.string().nullable(),
  headlineSummary: z.string().nullable(),
  createdDate: z.string().nullable(),
  metrics: z.array(SnapshotMetricSchema),
});
export type SnapshotDetail = z.infer<typeof SnapshotDetailSchema>;

export const SnapshotsResponseSchema = z.object({
  latest: SnapshotDetailSchema.nullable(),
  history: z.array(SnapshotSummarySchema),
});
export type SnapshotsResponse = z.infer<typeof SnapshotsResponseSchema>;

export const MetricHistoryResponseSchema = z.object({ history: MetricHistoryMapSchema });
export type MetricHistoryResponse = z.infer<typeof MetricHistoryResponseSchema>;

const OwnedAccountOpenOpportunitySchema = z.object({
  id: z.string(),
  name: z.string(),
  stage: z.string().nullable(),
  closeDate: z.string().nullable(),
  createdDate: z.string().nullable(),
});
export type OwnedAccountOpenOpportunity = z.infer<typeof OwnedAccountOpenOpportunitySchema>;

const OwnedAccountLatestSnapshotSchema = z.object({
  id: z.string(),
  captureType: z.string().nullable(),
  createdDate: z.string().nullable(),
  totalAnnualValue: z.number().nullable(),
  headlineSummary: z.string().nullable(),
});
export type OwnedAccountLatestSnapshot = z.infer<typeof OwnedAccountLatestSnapshotSchema>;

const OwnedAccountSchema = z.object({
  /** 18-char Salesforce account id. */
  id: z.string(),
  name: z.string(),
  openOpportunities: z.array(OwnedAccountOpenOpportunitySchema),
  latestSnapshot: OwnedAccountLatestSnapshotSchema.nullable(),
  /** ISO date string used for client-side sorting; null when the account has neither a snapshot nor an open opp. */
  recencyKey: z.string().nullable(),
});
export type OwnedAccount = z.infer<typeof OwnedAccountSchema>;

export const OwnedAccountsResponseSchema = z.object({ accounts: z.array(OwnedAccountSchema) });
export type OwnedAccountsResponse = z.infer<typeof OwnedAccountsResponseSchema>;

const CatalogDefinitionSchema = z.object({
  id: z.string(),
  metricKey: z.string().nullable(),
  name: z.string().nullable(),
  unit: z.string().nullable(),
  active: z.boolean().nullable(),
});
export type CatalogDefinition = z.infer<typeof CatalogDefinitionSchema>;

export const CatalogResponseSchema = z.object({ definitions: z.array(CatalogDefinitionSchema) });
export type CatalogResponse = z.infer<typeof CatalogResponseSchema>;

/** POST /snapshots body. The client never reads it (the write is already committed by the time it is sent). */
export const SaveSnapshotResultSchema = z.object({
  success: z.boolean(),
  snapshotId: z.string().optional(),
  warnings: z.array(z.string()),
  /** The lifecycle phase the server inferred and wrote (Capture_Type__c). */
  captureType: z.string().optional(),
});
export type SaveSnapshotResult = z.infer<typeof SaveSnapshotResultSchema>;
